import { HttpErrorResponse } from '@angular/common/http';
import {
  ApiErrorResponse,
  ApiHttpError,
  ApiValidationErrorResponse,
} from '@shared/models/api-error';

function parseErrorPayload(payload: unknown): unknown {
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch {
      return payload;
    }
  }

  return payload;
}

export function isApiValidationError(
  error: unknown,
): error is ApiValidationErrorResponse {
  if (!error || typeof error !== 'object') return false;

  const candidate = error as Record<string, unknown>;

  return (
    typeof candidate['message'] === 'string' &&
    !!candidate['errors'] &&
    typeof candidate['errors'] === 'object' &&
    !Array.isArray(candidate['errors'])
  );
}

export function isApiErrorResponse(error: unknown): error is ApiErrorResponse {
  if (!error || typeof error !== 'object') return false;

  const candidate = error as Record<string, unknown>;

  return typeof candidate['message'] === 'string';
}

export function extractApiError(err: HttpErrorResponse): ApiHttpError | null {
  const parsedError = parseErrorPayload(err.error);

  if (isApiValidationError(parsedError)) return parsedError;
  if (isApiErrorResponse(parsedError)) return parsedError;

  return null;
}

export function extractApiErrorMessage(
  err: HttpErrorResponse,
  fallback = 'Ocurrió un error inesperado',
): string {
  const parsedError = parseErrorPayload(err.error);

  if (isApiValidationError(parsedError)) return parsedError.message;
  if (isApiErrorResponse(parsedError)) return parsedError.message;

  return fallback;
}

/**
 * Une errores planos (`{ email: [...] }`) con grupos anidados (`{ user: { currentPassword: [...] } }`)
 * para poder mapearlos a controles del formulario con los mismos nombres de campo.
 */
function flattenValidationErrors(errors: Record<string, unknown>): Record<string, string[]> {
  const result: Record<string, string[]> = {};

  for (const [key, value] of Object.entries(errors)) {
    if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
      result[key] = value;
      continue;
    }

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const nested = value as Record<string, unknown>;
      for (const [nestedKey, nestedVal] of Object.entries(nested)) {
        if (Array.isArray(nestedVal) && nestedVal.every((v) => typeof v === 'string')) {
          result[nestedKey] = nestedVal;
        }
      }
    }
  }

  return result;
}

export function extractValidationErrors(
  err: HttpErrorResponse,
): Record<string, string[]> | null {
  const parsedError = parseErrorPayload(err.error);

  if (isApiValidationError(parsedError)) {
    return flattenValidationErrors(parsedError.errors as Record<string, unknown>);
  }

  return null;
}
